import React, { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updateProfile: (data: { displayName?: string; avatarUrl?: string }) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  activeDates: Set<string>;
  refreshActiveDates: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeDates, setActiveDates] = useState<Set<string>>(new Set());

  const fetchActiveDates = async (userId: string) => {
    const { data, error } = await supabase
      .from('entries')
      .select('created_at')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching active dates:', error);
      return;
    }

    const dates = new Set<string>();
    data?.forEach(entry => {
      // Create date object from UTC string
      const date = new Date(entry.created_at);
      // Format to YYYY-MM-DD in local time
      const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      dates.add(dateString);
    });

    setActiveDates(dates);
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchActiveDates(session.user.id);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchActiveDates(session.user.id);
      } else {
        setActiveDates(new Set());
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Global Sync: Ensure session metadata matches database profile
  useEffect(() => {
    const syncProfileMetadata = async () => {
      if (!user) return;

      // Check authoritative profile data
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('display_name, avatar_url')
        .eq('id', user.id)
        .single();

      if (error || !profile) return;

      const metadata = user.user_metadata || {};
      const currentAvatar = metadata.avatar_url;
      const currentName = metadata.display_name;

      // Check for mismatch
      const needsUpdate = 
        (profile.avatar_url && profile.avatar_url !== currentAvatar) ||
        (profile.display_name && profile.display_name !== currentName);

      if (needsUpdate) {
        const { data: { user: updatedUser } } = await supabase.auth.updateUser({
          data: {
            avatar_url: profile.avatar_url,
            display_name: profile.display_name
          }
        });
        
        // Update local state if successful to reflect changes instantly in UI
        if (updatedUser) {
          setUser(updatedUser);
        }
      }
    };

    // Run sync when user ID changes (login) or when specifically mounting with a user
    syncProfileMetadata();
  }, [user]);

  const signUp = async (email: string, password: string, displayName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName,
        },
      },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const updateProfile = async (data: { displayName?: string; avatarUrl?: string }) => {
    const updates: { display_name?: string; avatar_url?: string } = {};
    if (data.displayName !== undefined) updates.display_name = data.displayName;
    if (data.avatarUrl !== undefined) updates.avatar_url = data.avatarUrl;

    // Update auth.users metadata
    const { error } = await supabase.auth.updateUser({
      data: updates,
    });

    // Also update the profiles table for consistency
    if (!error && user) {
      const profileUpdates: { display_name?: string; avatar_url?: string } = {};
      if (data.displayName !== undefined) profileUpdates.display_name = data.displayName;
      if (data.avatarUrl !== undefined) profileUpdates.avatar_url = data.avatarUrl;
      
      await supabase
        .from('profiles')
        .upsert({ id: user.id, ...profileUpdates });
    }

    if (!error) {
      // Manually update local user state to reflect changes immediately
      setUser((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          user_metadata: {
            ...prev.user_metadata,
            ...updates,
          },
        } as User;
      });
    }
    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}`,
      },
    });
    return { error: error as Error | null };
  };

  const refreshActiveDates = async () => {
    if (user) {
      await fetchActiveDates(user.id);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut, updateProfile, signInWithGoogle, activeDates, refreshActiveDates }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
